import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useClawWs, getBaseKeyModule } from '@src/hooks/useClawWs';
import { ClawHome } from '@src/components/pc/Claw/Home';
import useClawWsStore from '@src/stores/useClawWsStore';
import { PIN_STORAGE_KEY } from '@src/stores/useClawWsStore';
import { SIDEBAR_EXPAND_KEY } from '@src/stores/useLayoutStore';
import { useLayoutStore } from '@src/stores/useLayoutStore';
import { preloadPixelOfficeAssets } from '@src/components/pc/Claw/PixelOffice/PixelOfficeCanvas';
import {
  buildMainagentPath,
  MAINAGENT_ROUTE_BASE,
} from '@src/utils/mainagent-route';
import './index.less';

export default function ClawIndex() {
  const { sessionKey: urlSessionKey } = useParams<{ sessionKey?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { fetchSessions, switchSession } = useClawWs();
  const { currentSessionKey, sessions, connStatus, setCurrentSessionKey, clearMessages } = useClawWsStore();
  const initialPrompt = ((location.state as { initialPrompt?: string } | null)?.initialPrompt ?? '').trim();

  useEffect(() => {
    if (!localStorage) return;
    // 如果进来 两个 key 都为未定义 
    if (localStorage.getItem(PIN_STORAGE_KEY) === null && localStorage.getItem(SIDEBAR_EXPAND_KEY) === null) {
      // 收起左边
      useLayoutStore.getState().setSidebarExpand(false);
    }
    // 进入 /mainagent 路由时立即后台预加载像素办公室资源
    void preloadPixelOfficeAssets();
  }, []);

  // 初始化选会话时只做一次，用 ref 防止重复触发
  const initializedRef = useRef(false);
  const handledStateKeyRef = useRef<string | null>(null);

  // 从创建页带 initialPrompt 进入时，强制进入新会话（/mainagent）
  useEffect(() => {
    if (!initialPrompt) return;
    if (handledStateKeyRef.current === location.key) return;
    handledStateKeyRef.current = location.key;

    if (currentSessionKey) {
      clearMessages();
      setCurrentSessionKey(null);
    }
    if (window.location.pathname !== MAINAGENT_ROUTE_BASE) {
      navigate(MAINAGENT_ROUTE_BASE, { replace: true, state: location.state });
    }
  }, [
    clearMessages,
    currentSessionKey,
    initialPrompt,
    location.key,
    location.state,
    navigate,
    setCurrentSessionKey,
  ]);

  // 连接成功后，根据 URL 参数恢复上次选中的会话
  useEffect(() => {
    if (!urlSessionKey || connStatus !== 'connected' || initializedRef.current) return;
    initializedRef.current = true;

    const trySwitch = (list: typeof sessions) => {
      const match = list.find((s) => getBaseKeyModule(s.key) === urlSessionKey);
      if (match) switchSession(match.key);
    };

    // 已有缓存列表时直接匹配，否则先拉取
    if (sessions.length > 0) {
      trySwitch(sessions);
    } else {
      fetchSessions()
        .then(trySwitch)
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connStatus]);

  // currentSessionKey 变化时同步到 URL（replace 避免浏览器历史堆积）
  useEffect(() => {
    // URL 上有 sessionKey 但当前会话尚未恢复时，不要提前跳走，等 connect 后再恢复
    if (!currentSessionKey && urlSessionKey) return;

    const baseKey = currentSessionKey ? getBaseKeyModule(currentSessionKey) : null;
    const targetPath = buildMainagentPath(baseKey);
    if (window.location.pathname !== targetPath) {
      // 透传 location.state，避免覆盖 initialPrompt 等跳转携带的参数
      navigate(targetPath, { replace: true, state: location.state ?? undefined });
    }
  }, [currentSessionKey, location.state, navigate, urlSessionKey]);

  return (
    <div className="mario-claw-index">
      <ClawHome />
    </div>
  );
}
